import { useEffect, useMemo, useRef, useState } from 'react';
import EmptyState from '@/components/common/EmptyState.jsx';
import { escanearEtiqueta } from '@/services/etiquetas.js';
import {
  CargasPage,
  CargasSection,
  CargasTableShell,
  CargasViewport,
  cargasInputClassName,
} from './components/CargasLayout.jsx';

const TIPOS = [
  { value: 'CARGA', label: 'Carga (salida a foja/vehículo)' },
  { value: 'DESCARGA', label: 'Descarga' },
  { value: 'RECEPCION', label: 'Recepción en destino' },
  { value: 'CONTROL', label: 'Control en depósito' },
];
const TIPO_STORAGE_KEY = 'sinamon.cargas.controlBultos.tipo';

function readStoredTipo() {
  try {
    const stored = window.localStorage.getItem(TIPO_STORAGE_KEY);
    return TIPOS.some(tipo => tipo.value === stored) ? stored : 'CARGA';
  } catch {
    return 'CARGA';
  }
}

const bulto = etiqueta => `${etiqueta.bultoNro || 1}/${etiqueta.bultosTotal || 1}`;

/**
 * Scanner-first control of physical bultos: the input keeps focus, Enter (sent by the
 * scanner) registers the label, and each lote shows how many bultos are still missing.
 */
export default function ControlBultos() {
  const inputRef = useRef(null);
  const [tipo, setTipo] = useState(readStoredTipo);
  const [codigo, setCodigo] = useState('');
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);
  const [lotes, setLotes] = useState({});
  const [incidencias, setIncidencias] = useState([]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const changeTipo = value => {
    setTipo(value);
    setLotes({});
    setIncidencias([]);
    setLast(null);
    try {
      window.localStorage.setItem(TIPO_STORAGE_KEY, value);
    } catch {
      // Remembering the control type is a convenience only.
    }
    inputRef.current?.focus();
  };

  const registrar = async () => {
    const value = codigo.trim();
    setCodigo('');
    inputRef.current?.focus();
    if (!value || busy) return;
    setBusy(true);
    try {
      const result = await escanearEtiqueta(value, tipo);
      const { etiqueta } = result;
      const key = etiqueta.loteId || etiqueta.id;
      setLotes(prev => ({
        ...prev,
        [key]: {
          codigo: prev[key]?.codigo || etiqueta.codigoEtiqueta,
          guiaNumero: etiqueta.guiaNumero,
          destinatario: etiqueta.destinatarioNombre,
          escaneados: result.loteEscaneados,
          total: result.loteTotal,
          updatedAt: Date.now(),
        },
      }));
      if (result.duplicado) {
        const message = `Ya estaba escaneada · ${etiqueta.codigoEtiqueta} (bulto ${bulto(etiqueta)})`;
        setLast({ type: 'warning', message });
        setIncidencias(prev => [{ id: `${Date.now()}-${value}`, codigo: value, message }, ...prev]);
      } else {
        setLast({ type: 'success', message: `Bulto ${bulto(etiqueta)} registrado · ${etiqueta.codigoEtiqueta}` });
      }
    } catch (error) {
      const message = error?.message || 'No se pudo registrar la etiqueta.';
      setLast({ type: 'error', message });
      setIncidencias(prev => [{ id: `${Date.now()}-${value}`, codigo: value, message }, ...prev]);
    } finally {
      setBusy(false);
      inputRef.current?.focus();
    }
  };

  const lotesOrdenados = useMemo(
    () => Object.entries(lotes).sort(([, a], [, b]) => b.updatedAt - a.updatedAt),
    [lotes],
  );
  const pendientes = lotesOrdenados.reduce((acc, [, lote]) => acc + Math.max(0, lote.total - lote.escaneados), 0);

  const feedbackClass = {
    success: 'border-emerald-500/50 bg-emerald-500/10',
    warning: 'border-amber-500/50 bg-amber-500/10',
    error: 'border-red-500/50 bg-red-500/10',
  };

  return (
    <CargasPage
      title="Control de bultos"
      description="Escaneá las etiquetas de bulto. Cada código se registra una vez por tipo de control."
      breadcrumbs={[{ label: 'Cargas', href: '/cargas/dashboard' }, { label: 'Control de bultos' }]}
      hideHeaderOnCompact
    >
      <CargasViewport className="grid grid-cols-12 gap-3">
        <CargasSection title="Escaneo" className="col-span-12 lg:col-span-5" bodyClassName="space-y-3 p-4">
          <label className="block text-sm">
            <span className="mb-1 block text-[rgba(var(--muted),0.92)]">Control</span>
            <select
              aria-label="Control"
              value={tipo}
              onChange={event => changeTipo(event.target.value)}
              className={cargasInputClassName}
            >
              {TIPOS.map(item => (
                <option key={item.value} value={item.value}>{item.label}</option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-[rgba(var(--muted),0.92)]">Código de la etiqueta</span>
            <input
              ref={inputRef}
              aria-label="Código de la etiqueta"
              value={codigo}
              autoComplete="off"
              spellCheck={false}
              onChange={event => setCodigo(event.target.value)}
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  void registrar();
                }
              }}
              className={`${cargasInputClassName} text-lg tracking-wide`}
              placeholder="Escaneá o escribí el código y Enter"
            />
          </label>
          <div
            role="status"
            aria-live="polite"
            className={`min-h-[3rem] rounded-xl border px-3 py-2 text-sm font-semibold text-[rgb(var(--fg))] ${last ? feedbackClass[last.type] : 'border-[rgba(var(--border),0.45)]'}`}
          >
            {last ? last.message : busy ? 'Registrando…' : 'Listo para escanear.'}
          </div>
          <p className="text-xs text-[rgba(var(--muted),0.92)]">
            {lotesOrdenados.length} lote(s) · {pendientes} bulto(s) pendientes
          </p>
        </CargasSection>

        <CargasSection title="Lotes escaneados" className="col-span-12 lg:col-span-7" bodyClassName="min-h-0 space-y-3 p-4">
          {lotesOrdenados.length === 0 ? (
            <EmptyState title="Sin escaneos" description="Los lotes aparecen al escanear su primer bulto." />
          ) : (
            <CargasTableShell className="max-h-[45vh]">
              <table className="min-w-full text-left text-sm">
                <thead className="sticky top-0 bg-[rgb(var(--panel))] text-[rgba(var(--muted),0.9)]">
                  <tr>
                    <th className="px-3 py-2">Lote</th>
                    <th className="px-3 py-2">Guía</th>
                    <th className="px-3 py-2">Destinatario</th>
                    <th className="px-3 py-2">Bultos</th>
                    <th className="px-3 py-2">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {lotesOrdenados.map(([key, lote]) => (
                    <tr key={key} className="border-t border-[rgba(var(--border),0.45)]">
                      <td className="px-3 py-2 font-semibold">{lote.codigo}</td>
                      <td className="px-3 py-2">{lote.guiaNumero || 'Pendiente'}</td>
                      <td className="px-3 py-2">{lote.destinatario}</td>
                      <td className="px-3 py-2">{lote.escaneados}/{lote.total}</td>
                      <td className="px-3 py-2">
                        {lote.escaneados >= lote.total ? 'Completo' : `Faltan ${lote.total - lote.escaneados}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CargasTableShell>
          )}

          {incidencias.length ? (
            <div>
              <h3 className="mb-1 text-sm font-semibold text-[rgb(var(--fg))]">Incidencias ({incidencias.length})</h3>
              <ul className="max-h-40 space-y-1 overflow-auto text-xs">
                {incidencias.map(item => (
                  <li key={item.id} className="rounded-lg border border-[rgba(var(--border),0.45)] px-2 py-1">
                    <span className="font-semibold">{item.codigo}</span>: {item.message}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </CargasSection>
      </CargasViewport>
    </CargasPage>
  );
}
